/**
 * Symbol Configuration Index
 *
 * Re-exports all symbol patterns and provides unified lookup functions.
 */

export * from './treasury.js';
export * from './municipal.js';
export * from './reit.js';
export * from './mlp.js';
export * from './moneyMarket.js';
export * from './companyTickerMap.js';
export * from './etfClassifications.js';

import { TREASURY_PATTERNS } from './treasury.js';
import { MUNICIPAL_PATTERNS } from './municipal.js';
import { REIT_SYMBOL_PATTERNS } from './reit.js';
import { MLP_SYMBOL_PATTERNS } from './mlp.js';
import { MONEY_MARKET_PATTERNS } from './moneyMarket.js';
import { COMPANY_TICKER_MAP } from './companyTickerMap.js';

// Re-export combined patterns for backward compatibility
export const ALL_TREASURY_PATTERNS = TREASURY_PATTERNS;
export const ALL_MUNICIPAL_PATTERNS = MUNICIPAL_PATTERNS;
export const ALL_REIT_PATTERNS = REIT_SYMBOL_PATTERNS;
export const ALL_MLP_PATTERNS = MLP_SYMBOL_PATTERNS;
export const ALL_MONEY_MARKET_PATTERNS = MONEY_MARKET_PATTERNS;

// ============================================================================
// Company Ticker Utilities
// ============================================================================

/**
 * Get ticker symbol for a company name
 * @param companyName - Company name (case-insensitive)
 * @returns Ticker symbol if found, null otherwise
 */
export function getCompanyTicker(companyName: string): string | null {
  const normalized = companyName.trim().toUpperCase();

  // Try exact match first
  if (COMPANY_TICKER_MAP[normalized]) {
    return COMPANY_TICKER_MAP[normalized];
  }

  // Try partial match (company name contains the key)
  for (const [name, ticker] of Object.entries(COMPANY_TICKER_MAP)) {
    if (normalized.includes(name)) {
      return ticker;
    }
  }

  return null;
}

/**
 * Get all company name entries for a ticker symbol
 * @param ticker - Ticker symbol
 * @returns Array of company name variants that map to this ticker
 */
export function getCompanyNamesForTicker(ticker: string): string[] {
  const upperTicker = ticker.toUpperCase();
  return Object.entries(COMPANY_TICKER_MAP)
    .filter(([_, t]) => t === upperTicker)
    .map(([name, _]) => name);
}

/**
 * Check if a company name has a known ticker mapping
 * @param companyName - Company name (case-insensitive)
 * @returns True if mapping exists
 */
export function hasCompanyMapping(companyName: string): boolean {
  return getCompanyTicker(companyName) !== null;
}

/**
 * Add a new company name to ticker mapping at runtime
 * Useful for user corrections or learning new mappings
 * @param companyName - Company name
 * @param ticker - Ticker symbol
 */
export function addCompanyMapping(companyName: string, ticker: string): void {
  COMPANY_TICKER_MAP[companyName.toUpperCase()] = ticker.toUpperCase();
  companyMappingVersion += 1;
}

let companyMappingVersion = 0;

/**
 * Bumped by every `addCompanyMapping`: memoized description parses depend on
 * the map and are dropped when it changes (LAT-05).
 */
export function getCompanyMappingVersion(): number {
  return companyMappingVersion;
}

/**
 * Get all available ticker mappings
 * @returns Record of company names to ticker symbols
 */
export function getAllCompanyMappings(): Record<string, string> {
  return { ...COMPANY_TICKER_MAP };
}

/**
 * Get count of known company mappings
 * @returns Number of company name variants in the mapping
 */
export function getCompanyMappingCount(): number {
  return Object.keys(COMPANY_TICKER_MAP).length;
}
